import { useEffect } from 'react';

export type SeoSchema = Record<string, unknown>;

export interface SeoProps {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  schema?: SeoSchema | SeoSchema[];
}

export const SITE_URL = 'https://leavittpeakpress.com';

export const DEFAULT_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Elegant%20editorial%20brand%20image%20for%20an%20independent%20book%20publisher%2C%20warm%20cream%20and%20deep%20navy%20tones%20with%20subtle%20gold%20light%2C%20stacked%20hardcover%20books%20and%20soft%20paper%20textures%20on%20a%20clean%20minimal%20surface%2C%20refined%20literary%20atmosphere%2C%20premium%20photographic%20quality%2C%20generous%20negative%20space%2C%20no%20readable%20text&width=1200&height=630&seq=leavitt-peak-press-og-2026&orientation=landscape';

/**
 * Keeps <head> in sync with the current route: title, description, keywords,
 * canonical, social cards and optional JSON-LD. Every value is restored on
 * unmount so no page leaks metadata into the next one.
 */
export default function Seo({
  title,
  description,
  keywords,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  robots = 'index, follow',
  schema,
}: SeoProps) {
  useEffect(() => {
    const cleanups: Array<() => void> = [];

    const previousTitle = document.title;
    document.title = title;
    cleanups.push(() => {
      document.title = previousTitle;
    });

    const rememberAttribute = (element: Element, attribute: string, value: string) => {
      const previous = element.getAttribute(attribute);
      element.setAttribute(attribute, value);
      cleanups.push(() => {
        if (previous === null) {
          element.removeAttribute(attribute);
        } else {
          element.setAttribute(attribute, previous);
        }
      });
    };

    const setMeta = (attribute: 'name' | 'property', key: string, content: string) => {
      const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      if (existing) {
        rememberAttribute(existing, 'content', content);
        return;
      }
      const meta = document.createElement('meta');
      meta.setAttribute(attribute, key);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      cleanups.push(() => meta.remove());
    };

    const canonicalUrl = `${SITE_URL}${path}`;

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    setMeta('name', 'robots', robots);

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:site_name', 'Leavitt Peak Press');
    setMeta('property', 'og:locale', 'en_US');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);

    const existingCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (existingCanonical) {
      rememberAttribute(existingCanonical, 'href', canonicalUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.setAttribute('href', canonicalUrl);
      document.head.appendChild(link);
      cleanups.push(() => link.remove());
    }

    if (schema) {
      const payload = Array.isArray(schema)
        ? { '@context': 'https://schema.org', '@graph': schema }
        : { '@context': 'https://schema.org', ...schema };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'page-structured-data';
      script.textContent = JSON.stringify(payload);
      document.head.appendChild(script);
      cleanups.push(() => script.remove());
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, keywords, path, image, type, robots, schema]);

  return null;
}